import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import sendFormDataJson from "../utils/sendFormDataJson";

export default function Registration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [emailValidityMsg, setEmailValidityMsg] = useState("");

  async function handleSubmit(evt) {
    evt.preventDefault();
    const resp = await sendFormDataJson(evt);
    if (resp.ok) {
      navigate("/login");
    } else {
      alert(resp.statusText || "Fehler bei der Registrierung");
      console.error("Registration failed:", resp);
    }
  }

  const inviteQuery = useQuery({
    queryKey: ["inviteData"],
    queryFn: async () => {
      const response = await fetch(`/api/invites/${searchParams.get("token")}`);
      if (!response.ok) {
        throw new Error("Failed to fetch invite data");
      }
      return response.json();
    },
    initialData: [],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: searchParams.get("token") !== null,
  });

  useEffect(() => {
    if (inviteQuery.status === "success" && email === "") {
      setEmail(inviteQuery.data.invitee_email || "");
    }
  }, [inviteQuery.data]);

  async function checkEmailAvailability(email) {
    const resp = await fetch(
      `/api/check-email?email=${encodeURIComponent(email)}`
    );
    return resp;
  }

  async function emailChange(evt) {
    setEmail(evt.target.value);
    setEmailValidityMsg("");
    if (evt.target.checkValidity()) {
      const resp = await checkEmailAvailability(evt.target.value);
      if (resp.ok && (await resp.json()).exists) {
        setEmailValidityMsg(
          "Diese E-Mail-Adresse ist bereits registriert. Bitte eine andere E-Mail-Adresse verwenden."
        );
      } else {
        if (resp.status === 422) {
          setEmailValidityMsg("Ungültige E-Mail-Adresse");
        }
      }
    } else {
      setEmailValidityMsg(evt.target.validationMessage);
    }
  }

  if (inviteQuery.status === "error") {
    return (
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
            Einladung ungültig
          </h2>
          <p className="mt-2 text-center text-sm/6 text-gray-500">
            Die Einladung ist ungültig oder abgelaufen.
          </p>
        </div>
      </div>
    );
  }
  return (
    <>
      {/*
          This example requires updating your template:
  
          ```
          <html class="h-full bg-white">
          <body class="h-full">
          ```
        */}
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img
            alt="bookingal"
            src="https://tailwindui.com/plus/img/logos/mark.svg?color=indigo&shade=600"
            className="mx-auto h-10 w-auto"
          />
          <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
            {inviteQuery.data?.name
              ? inviteQuery.data.name
              : "Registriere dich für bookingal"}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <form
            action="/api/registration"
            method="POST"
            className="space-y-6"
            onSubmit={handleSubmit}
          >
            <div>
              <label
                htmlFor="email"
                className="block text-sm/6 font-medium text-gray-900"
              >
                Email Adresse
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={emailChange}
                  className="peer block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 
                  outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6
                  invalid:not-focus:outline-red-500"
                />
                {emailValidityMsg && (
                  <p className="mt-2 text-sm text-red-600">
                    {emailValidityMsg}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label
                htmlFor="name"
                className="block text-sm/6 font-medium text-gray-900"
              >
                Name
              </label>
              <div className="mt-2">
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />
              </div>
            </div>
            {!searchParams.get("token") ? (
              <div>
                <label
                  htmlFor="organisation"
                  className="block text-sm/6 font-medium text-gray-900"
                >
                  Organisation
                </label>
                <div className="mt-2">
                  <input
                    id="organisation"
                    name="organisation"
                    type="text"
                    required
                    className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  />
                </div>
              </div>
            ) : (
              <input
                type="hidden"
                name="invite"
                value={searchParams.get("token")}
              />
            )}

            <div>
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-sm/6 font-medium text-gray-900"
                >
                  Passwort
                </label>
              </div>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-sm/6 font-medium text-gray-900"
                >
                  Passwortwiederholung
                </label>
              </div>
              <div className="mt-2">
                <input
                  id="password-confirmation"
                  name="password-confirmation"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                />
              </div>
            </div>
            <div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                Registrieren
              </button>
            </div>
          </form>
          <a
            href="/admin"
            className="mt-6 block text-center text-sm/6 text-gray-500 hover:text-gray-700"
          >
            Bereits registriert? Melde dich an
          </a>
        </div>
      </div>
    </>
  );
}
